/**
 * batch-56 klantenportaal (FO-413) — gedeelde types, labels en Zod-schemas voor
 * de verbeterronde D1-D8.
 *
 * Eén bron voor API, backoffice en klant-app: de API valideert met exact de
 * schemas die de formulieren client-side gebruiken, zodat een scherm nooit iets
 * kan sturen dat de API weigert (CLAUDE.md "Validatie & foutmeldingen").
 *
 * NL-labels horen bij de BACKOFFICE-schermen. De klant-app is tweetalig en
 * haalt zijn teksten uit next-intl (apps/klant/messages/{nl,en}.json) — nooit
 * uit deze labels.
 */
import { z } from 'zod'
import { DOCUMENT_CATEGORIE } from './documenten'

// ─── D1 — rondleiding ────────────────────────────────────────────────────────

/**
 * Versie van de rondleiding. Ophogen = iedereen krijgt de tour opnieuw
 * (bewuste actie bij een inhoudelijke herziening van het portaal).
 */
export const KLANT_TOUR_VERSIE = 1

/**
 * De stappen van de rondleiding, config-gedreven (spec §12.1: lichtgewicht
 * eigen component, geen tour-library). Elke stap heeft een i18n-sleutel onder
 * de namespace `tour.stappen.<key>` en de route waar hij naartoe wijst.
 *
 * `onderdeel` koppelt een stap aan een uitschakelbaar portaal-onderdeel (D3/D6/
 * D7/D8): staat dat voor deze klant uit, dan slaat de rondleiding de stap over.
 * Anders zou de klant uitleg krijgen over een pagina die hij niet heeft.
 */
export const KLANT_TOUR_STAPPEN = [
  { key: 'werkbonnen', href: '/werkbonnen', onderdeel: null },
  { key: 'planning', href: '/planning', onderdeel: null },
  { key: 'facturen', href: '/facturen', onderdeel: 'facturen' },
  { key: 'meldingen', href: '/notificaties', onderdeel: null },
  { key: 'account', href: '/account', onderdeel: null },
] as const
export type KlantTourStapKey = (typeof KLANT_TOUR_STAPPEN)[number]['key']
export type KlantTourStap = (typeof KLANT_TOUR_STAPPEN)[number]

/** De portaal-onderdelen die per klant aan/uit kunnen (zie `nav-info`). */
export interface KlantPortaalOnderdelen {
  facturen: boolean
  documenten: boolean
  gereedschap: boolean
  monteurkeuze: boolean
}

/** De stappen die deze klant daadwerkelijk kan volgen. */
export function zichtbareTourStappen(
  onderdelen: Partial<KlantPortaalOnderdelen> | null | undefined,
): readonly KlantTourStap[] {
  return KLANT_TOUR_STAPPEN.filter(
    (stap) => stap.onderdeel === null || onderdelen?.[stap.onderdeel] !== false,
  )
}

export const klantTourAfrondenSchema = z.object({
  /** true = de klant heeft de tour weggeklikt i.p.v. afgerond. */
  overgeslagen: z.boolean().default(false),
})
export type KlantTourAfrondenInput = z.infer<typeof klantTourAfrondenSchema>

// ─── D2 — gegevens-controlestap ──────────────────────────────────────────────

export const KLANT_CONTROLE_BLOKKEN = ['FACTURATIE', 'ROOSTER', 'PAUZE', 'SLUITINGSDAGEN'] as const
export type KlantControleBlok = (typeof KLANT_CONTROLE_BLOKKEN)[number]

/** Backoffice-labels (relatie-detail). De klant-app gebruikt next-intl. */
export const KLANT_CONTROLE_BLOK_LABELS: Record<KlantControleBlok, string> = {
  FACTURATIE: 'Facturatiegegevens',
  ROOSTER: 'Rooster en werktijden',
  PAUZE: 'Pauzetijden',
  SLUITINGSDAGEN: 'Sluitingsdagen',
}

export const klantControleBevestigSchema = z.object({
  blok: z.enum(KLANT_CONTROLE_BLOKKEN),
  opmerking: z.string().trim().max(2000).optional().nullable(),
})
export type KlantControleBevestigInput = z.infer<typeof klantControleBevestigSchema>

export const klantControleVoorstelSchema = z.object({
  blok: z.enum(KLANT_CONTROLE_BLOKKEN),
  omschrijving: z
    .string()
    .trim()
    .min(5, 'Beschrijf kort wat er niet klopt (minimaal 5 tekens)')
    .max(5000),
})
export type KlantControleVoorstelInput = z.infer<typeof klantControleVoorstelSchema>

// ─── D3 — facturen-zichtbaarheid ─────────────────────────────────────────────

export const KLANT_FACTUREN_MODI = ['ALLES', 'ALLEEN_DEFINITIEF', 'VANAF_DATUM'] as const
export type KlantFacturenModus = (typeof KLANT_FACTUREN_MODI)[number]

export const KLANT_FACTUREN_MODUS_LABELS: Record<KlantFacturenModus, string> = {
  ALLES: 'Alles (huidige gedrag)',
  ALLEEN_DEFINITIEF: 'Alleen definitieve facturen',
  VANAF_DATUM: 'Alles vanaf een datum',
}

// ─── D5 — ervaringscategorie ─────────────────────────────────────────────────

export const KLANT_ERVARING_CATEGORIE_LABELS: Record<
  'INCIDENTEEL' | 'REGELMATIG' | 'INGEWERKT',
  string
> = {
  INCIDENTEEL: 'Incidenteel',
  REGELMATIG: 'Regelmatig',
  INGEWERKT: 'Ingewerkt',
}

/** Maximum aantal certificaten op de compacte klant-monteurkaart (D4). */
export const KLANT_CERTIFICAAT_MAX = 5

// ─── Backoffice: per-klant portaal-instellingen (D3/D5/D6/D7/D8) ─────────────

export const klantPortaalInstellingenSchema = z
  .object({
    portaalFacturenZichtbaar: z.boolean(),
    portaalFacturenModus: z.enum(KLANT_FACTUREN_MODI),
    // ISO-datum (yyyy-MM-dd) of null; alleen betekenisvol bij VANAF_DATUM.
    portaalFacturenVanaf: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in het formaat jjjj-mm-dd')
      .nullable()
      .optional(),
    portaalErvaringGrensRegelmatig: z.coerce.number().int().min(0).max(100000),
    portaalErvaringGrensIngewerkt: z.coerce.number().int().min(0).max(100000),
    // Tegen de enum valideren: een onbekende categorie hoort een 400 met inline
    // veldfout te geven, niet een Prisma-fout (500) verderop.
    portaalDocumentCategorieenAuto: z.array(z.enum(DOCUMENT_CATEGORIE)).max(50),
    portaalControleIntervalMaanden: z.coerce.number().int().min(0).max(120),
    portaalGereedschapZichtbaar: z.boolean(),
    portaalMonteurkeuzeZichtbaar: z.boolean(),
    // ── FO-304-H / FO-308-P2a/P2c/P2d — velden die al in het datamodel zaten en
    // door de API gebruikt werden, maar nergens een formulier hadden. Ze stonden
    // dus vast op hun default tenzij iemand de database aanpaste. Optioneel in
    // het schema zodat een oudere client die ze niet stuurt niets overschrijft.
    //
    // Default-zichtbaarheid bij UITGIFTE van een nieuwe factuur/offerte; een
    // latere handmatige per-stuk-toggle wint hier altijd van.
    factuurPortaalDefaultZichtbaar: z.boolean().optional(),
    offertePortaalDefaultZichtbaar: z.boolean().optional(),
    // Hoeveel dagen vóór de einddatum de contract-afloopmail naar deze klant
    // gaat. `null` = de standaard van 30 dagen.
    contractAfloopKlantMailDagen: z.coerce
      .number()
      .int()
      .min(1, 'Kies minimaal 1 dag')
      .max(365, 'Kies maximaal 365 dagen')
      .nullable()
      .optional(),
    // Mag een klant-OWNER zelf portaalgebruikers aanmaken en intrekken?
    zelfbeheerGebruikersActief: z.boolean().optional(),
  })
  .refine((v) => v.portaalErvaringGrensIngewerkt >= v.portaalErvaringGrensRegelmatig, {
    message: 'De grens voor "Ingewerkt" moet minstens zo hoog zijn als die voor "Regelmatig"',
    path: ['portaalErvaringGrensIngewerkt'],
  })
  .refine((v) => v.portaalFacturenModus !== 'VANAF_DATUM' || !!v.portaalFacturenVanaf, {
    message: 'Kies een datum wanneer je "Alles vanaf een datum" instelt',
    path: ['portaalFacturenVanaf'],
  })
export type KlantPortaalInstellingenInput = z.infer<typeof klantPortaalInstellingenSchema>

// ─── D6 — document-vrijgave ──────────────────────────────────────────────────

export const documentKlantportaalVrijgaveSchema = z
  .object({
    /** true = vrijgeven, false = intrekken. */
    vrijgeven: z.boolean(),
    reden: z.string().trim().max(1000).optional().nullable(),
  })
  // batch-60 (FO-439b K1) — intrekken is een bewuste uitzondering MET reden
  // (zelfde regel als de relatie-tab: `klantStukVrijgaveSchema`).
  .refine((v) => v.vrijgeven || (v.reden ?? '').trim().length >= 3, {
    message: 'Geef een reden om dit document voor de klant in te trekken',
    path: ['reden'],
  })
export type DocumentKlantportaalVrijgaveInput = z.infer<typeof documentKlantportaalVrijgaveSchema>

// ─── D7 — gereedschap ────────────────────────────────────────────────────────

export const klantGereedschapItemSchema = z.object({
  naam: z.string().trim().min(2, 'Vul een naam in').max(200),
  omschrijving: z.string().trim().max(2000).optional().nullable(),
  aantal: z.coerce.number().int().min(0).max(100000).optional().nullable(),
  locatieId: z.string().trim().min(1).optional().nullable(),
  voorraadArtikelId: z.string().trim().min(1).optional().nullable(),
  actief: z.boolean().default(true),
  volgorde: z.coerce.number().int().min(0).max(10000).default(0),
})
export type KlantGereedschapItemInput = z.infer<typeof klantGereedschapItemSchema>

export const klantGereedschapVerzoekSchema = z.object({
  gereedschapItemId: z.string().trim().min(1).optional().nullable(),
  omschrijving: z
    .string()
    .trim()
    .min(5, 'Beschrijf kort wat u nodig heeft (minimaal 5 tekens)')
    .max(5000),
  gewensteDatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in het formaat jjjj-mm-dd')
    .optional()
    .nullable(),
})
export type KlantGereedschapVerzoekInput = z.infer<typeof klantGereedschapVerzoekSchema>

// ─── D8 — gewenst profiel + monteur-vrijgave/-reactie ────────────────────────

export const klantGewenstProfielSchema = z.object({
  skillIds: z.array(z.string().trim().min(1)).max(50),
  certificaatTypeIds: z.array(z.string().trim().min(1)).max(50),
  toelichting: z.string().trim().max(5000).optional().nullable(),
})
export type KlantGewenstProfielInput = z.infer<typeof klantGewenstProfielSchema>

export const KLANT_MONTEUR_REACTIE_SOORTEN = ['GRAAG', 'LIEVER_NIET', 'VRAAG'] as const
export type KlantMonteurReactieSoortKey = (typeof KLANT_MONTEUR_REACTIE_SOORTEN)[number]

export const KLANT_MONTEUR_REACTIE_SOORT_LABELS: Record<KlantMonteurReactieSoortKey, string> = {
  GRAAG: 'Graag deze monteur',
  LIEVER_NIET: 'Liever niet',
  VRAAG: 'Vraag over deze monteur',
}

export const klantMonteurReactieSchema = z.object({
  soort: z.enum(KLANT_MONTEUR_REACTIE_SOORTEN),
  toelichting: z.string().trim().max(2000).optional().nullable(),
})
export type KlantMonteurReactieInput = z.infer<typeof klantMonteurReactieSchema>

export const klantMonteurVrijgaveSchema = z.object({
  employeeId: z.string().trim().min(1, 'Kies een monteur'),
  toelichting: z.string().trim().max(2000).optional().nullable(),
})
export type KlantMonteurVrijgaveInput = z.infer<typeof klantMonteurVrijgaveSchema>

export const klantMonteurVrijgaveIntrekkenSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden voor het intrekken').max(1000),
})
export type KlantMonteurVrijgaveIntrekkenInput = z.infer<typeof klantMonteurVrijgaveIntrekkenSchema>

// ─── D7/D8 — werkvoorraad-statusbesluit (backoffice) ─────────────────────────

export const KLANT_VERZOEK_STATUSSEN = [
  'INGEDIEND',
  'IN_BEHANDELING',
  'GEREGELD',
  'AFGEWEZEN',
] as const
export type KlantVerzoekStatus = (typeof KLANT_VERZOEK_STATUSSEN)[number]

export const KLANT_VERZOEK_STATUS_LABELS: Record<KlantVerzoekStatus, string> = {
  INGEDIEND: 'Ingediend',
  IN_BEHANDELING: 'In behandeling',
  GEREGELD: 'Geregeld',
  AFGEWEZEN: 'Afgewezen',
}

/** Badge-varianten van `@streep/ui-fp` (de backoffice-Badge). */
export const KLANT_VERZOEK_STATUS_BADGE: Record<
  KlantVerzoekStatus,
  'default' | 'secondary' | 'warning' | 'success' | 'danger'
> = {
  INGEDIEND: 'secondary',
  IN_BEHANDELING: 'warning',
  GEREGELD: 'success',
  AFGEWEZEN: 'danger',
}

/**
 * Statusbesluit op een gereedschapsverzoek of monteur-reactie. Afwijzen vereist
 * een reden (spec §6.4: "afwijzen vereist reden") — dat is hier een harde
 * schema-regel, zodat de API het nooit zonder kan opslaan.
 */
export const klantVerzoekBesluitSchema = z
  .object({
    status: z.enum(KLANT_VERZOEK_STATUSSEN),
    afwijzingReden: z.string().trim().max(2000).optional().nullable(),
    behandelNotitie: z.string().trim().max(2000).optional().nullable(),
  })
  .refine((v) => v.status !== 'AFGEWEZEN' || (v.afwijzingReden ?? '').trim().length >= 3, {
    message: 'Geef een reden bij het afwijzen',
    path: ['afwijzingReden'],
  })
export type KlantVerzoekBesluitInput = z.infer<typeof klantVerzoekBesluitSchema>
